import { LOCATION_ASSIGN } from 'constants/appConstants';
import {
  ASK_DATA_ERROR,
  CHANGE_VOTE_ON_ASK_ITEM,
  MY_ASK_VOTES_ERROR,
  NEW_ASK_ITEM,
  RECEIVE_ASK_DATA,
  RECEIVE_MY_ASK_VOTES,
  REDIRECT,
  REPORT_ASK_ITEM,
  SET_ANSWERING_ASK_QUESTION,
  SET_ASK_ANSWERS_COLLAPSED,
  SET_ASK_HMD_SURVEY_DATA,
  SET_ASK_QUESTIONS_COLLAPSED,
  SET_VIEWING_ASK_QUESTIONS_DESKTOP
} from 'constants/reduxActions';
import api from 'apis/ask';
import { setResponseStatus } from 'actions/client';
import { trackError } from 'helpers/ErrorUtils';
import marketplace from 'cfg/marketplace.json';
import {
  FetchError,
  fetchErrorMiddleware,
  fetchErrorMiddlewareAllowedErrors
} from 'middleware/fetchErrorMiddleware';
import { buildAuthenticationRedirectUrl } from 'utils/redirect';
import { trackEvent } from 'helpers/analytics';
const {
  api: { ask: { url: askUrl, obfuscatedMarketplaceId: marketplaceId } }
} = marketplace;

export function askAuthRedirect(productId, colorId) {
  const redirectUrl = `/product/${productId}${colorId ? `/color/${colorId}` : ''}`;
  return {
    type: REDIRECT,
    location: buildAuthenticationRedirectUrl(redirectUrl),
    method: LOCATION_ASSIGN
  };
}

/**
 * Promise middleware that redirects if ZapposAsk gave us an error cause user
 * isn't authenticated.
 */
export function authRedirectMiddleware(dispatch, productId, colorId) {
  return function(response) {
    if (response.error === '401') {
      dispatch(askAuthRedirect(productId, colorId));
    }
    return response;
  };
}

function addVote(productId, colorId, qAItemId, delta) {
  return function(dispatch) {
    dispatch({ type: CHANGE_VOTE_ON_ASK_ITEM, data: { key: qAItemId, delta } });
    const fn = delta < 0 ? 'downvote' : 'upvote';
    api[fn](askUrl, marketplaceId, productId, qAItemId)
      .then(response => {
        if (response.status === 401) {
          dispatch(askAuthRedirect(productId, colorId));
        } else if (response.status >= 400) {
          throw new FetchError(response.url, response.status, response.statusText);
        }
        return response.json();
      });
  };
}

export function addAskUpvote(productId, colorId, qAItemId) {
  return addVote(productId, colorId, qAItemId, 1);
}

export function addAskDownvote(productId, colorId, qAItemId) {
  return addVote(productId, colorId, qAItemId, -1);
}

export function setAnsweringAskQuestion(key, answering) {
  return { type: SET_ANSWERING_ASK_QUESTION, data: { key, answering } };
}

export function setAskAnswersCollapsed(questionKey, collapsed) {
  return { type: SET_ASK_ANSWERS_COLLAPSED, data: { questionKey, collapsed } };
}

export function setAskQuestionsCollapsed(collapsed) {
  return { type: SET_ASK_QUESTIONS_COLLAPSED, data: { collapsed } };
}

export function setViewingAskQuestionsDesktop(viewing) {
  return { type: SET_VIEWING_ASK_QUESTIONS_DESKTOP, data: { viewing } };
}

export function submitAskItem(productId, colorId, parentQAItemId, text) {
  return function(dispatch) {
    if (parentQAItemId) {
      dispatch(setAnsweringAskQuestion(parentQAItemId, false));
      dispatch(setAskAnswersCollapsed(parentQAItemId, false));
    } else {
      dispatch(setViewingAskQuestionsDesktop(true));
    }
    api.writeItem(askUrl, marketplaceId, productId, parentQAItemId, text)
      .then(fetchErrorMiddleware)
      .then(authRedirectMiddleware(dispatch, productId, colorId))
      .then(response => {
        const data = { parentKey: parentQAItemId, newItem: response.qAItem };
        dispatch({ type: NEW_ASK_ITEM, data });
      });
  };
}

function receiveAskData(response) {
  const type = response.error ? ASK_DATA_ERROR : RECEIVE_ASK_DATA;
  return { type, data: response };
}

function receiveMyAskVotes(response) {
  const type = response.error ? MY_ASK_VOTES_ERROR : RECEIVE_MY_ASK_VOTES;
  return { type, data: response };
}

export function reportAskItem(productId, qAItemId) {
  api.reportItem(askUrl, marketplaceId, productId, qAItemId)
    .then(fetchErrorMiddleware);
  return { type: REPORT_ASK_ITEM, data: qAItemId };
}

export function fetchAsk(args) {
  const {
    localFetchData = api.fetchData,
    localFetchMyVotes = api.fetchMyVotes,
    productId
  } = args;

  return function(dispatch, getState) {
    const { cookies } = getState();
    const xMainCookie = cookies && cookies['x-main'];
    const shouldFetchMyVotes = !!xMainCookie;

    const data = localFetchData(askUrl, marketplaceId, productId)
      .then(fetchErrorMiddleware);

    if (!shouldFetchMyVotes) {
      return data.then(res => dispatch(receiveAskData(res)));
    }

    const votes = localFetchMyVotes(askUrl, marketplaceId, productId)
      .then(fetchErrorMiddlewareAllowedErrors([401]));

    return Promise.all([data, votes])
      .then(([dataRes, votesRes]) => {
        dispatch(receiveAskData(dataRes));
        dispatch(receiveMyAskVotes(votesRes));
      });
  };
}

export function setHmdSurveyMarkupData(data) {
  return {
    type: SET_ASK_HMD_SURVEY_DATA,
    data
  };
}

export function getHmdSurveyMarkupData({ pollId, key }, getHmdSurvey = api.getHmdSurvey) {
  return dispatch => getHmdSurvey(askUrl, { pollId, key })
    .then(fetchErrorMiddleware)
    .then(res => dispatch(setHmdSurveyMarkupData(res)))
    .catch(err => {
      trackError('ERROR', 'Failed to fetch HMD survey data', err);
      dispatch(setResponseStatus(404));
    });
}

export function submitHmdSurvey(data, submitHmdSurvey = api.submitHmdSurvey, performTrackEvent = trackEvent) {
  return dispatch => submitHmdSurvey(askUrl, data)
    .then(fetchErrorMiddleware)
    .then(res => {
      performTrackEvent('TE_HMD_SURVEY_SUBMITTED');
      return dispatch(setHmdSurveyMarkupData(res));
    })
    .catch(err => {
      trackError('ERROR', 'Failed to submit HMD survey data', err);
    });
}
